import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useElement,
    useListen,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { useStore } from '@atomify/kit';
import { useClickOutside } from '@bpd-library/utilities';
import { BPDFunctionalSvg } from '@source/components/atoms';
import { Filter, filterStore, updateFilter } from '@source/store/filters';
import { GTMPush } from '@source/utilities/gtm';

import { FILTER_NAMES } from '../property-filter';
import { transformTagLabel } from './utilities/transform-tag-label';

const DROPDOWN_WIDTH = 70;
const DROPDOWN_VISIBLE_CLASS = 'tag-list__dropdown--visible';
const COLLAPSING_DISABLED_CLASS = 'tag-list--collapse-disabled';

interface TagListElement extends Component {
    disableCollapse: boolean;
}

export const TagList: FC<TagListElement> = ({ element }) => {
    const [disableCollapse] = useProp<boolean>('disableCollapse', false);
    const wrapper = useElement<HTMLDivElement>('[js-hook-tag-list-wrapper]');
    const items = useElement<HTMLElement>('[js-hook-tag-list-items]');
    const dropdown = useElement<HTMLElement>('[js-hook-tag-list-dropdown]');

    const [, subscribe] = useStore(filterStore);

    onDidLoad(() => {
        appendTagList();

        useClickOutside(dropdown.current!, (clickOutside) => {
            if (clickOutside) hideDropdown();
        });

        if (disableCollapse) {
            element.classList.add(COLLAPSING_DISABLED_CLASS);
        }
    });

    subscribe(appendTagList, ['filters']);

    useListen(window, 'resize', appendTagList);

    function appendTagList() {
        // Draw tags
        renderTagList();

        // Hide overflowing tags
        !disableCollapse && handleOverflow();
    }

    function generateTags() {
        const { filters } = filterStore.getState();

        const tags: Filter[] = [];
        const price: Filter[] = [];

        Object.keys(filters).forEach((filterName) => {
            const { inputs, hasRangeValues } = filters[filterName];
            inputs.forEach(({ id, name, type, value, checked }) => {
                if (checked) {
                    if (FILTER_NAMES.priceRange === name) {
                        if (!price.length)
                            price.push({ id, type, value, name, hasRangeValues, checked });
                        if (price.length) price[0].value = `${price[0].value}.${value}`;
                    } else {
                        tags.push({ id, type, value, name, hasRangeValues, checked });
                    }
                }
            });
        });

        return [...tags, ...price];
    }

    function getOverflowElements(
        container: HTMLElement,
        children: NodeListOf<HTMLElement>,
    ): [Element[], Element[]] | undefined {
        const containerWidth = container.clientWidth;
        const containerOffsetLeft = container.offsetLeft;

        const visible: Element[] = [];
        const overflow: Element[] = [];

        if (!containerWidth || !children.length) return;

        Array.from(children).forEach((child) => {
            const { offsetLeft, clientWidth } = child;

            const isOverflowing =
                clientWidth + offsetLeft > containerWidth + containerOffsetLeft - DROPDOWN_WIDTH;

            if (isOverflowing) {
                overflow.push(child);
            } else {
                visible.push(child);
            }
        });

        return [visible, overflow];
    }

    function toggleDropdown() {
        const action = dropdown.current!.classList.contains(DROPDOWN_VISIBLE_CLASS)
            ? 'remove'
            : 'add';

        dropdown.current!.classList[action](DROPDOWN_VISIBLE_CLASS);
    }

    function hideDropdown() {
        dropdown.current!.classList.remove(DROPDOWN_VISIBLE_CLASS);
    }

    function handleOverflow() {
        dropdown.current!.innerHTML = '';

        const tags = element.querySelectorAll<HTMLElement>('[js-hook-tag-list-item]');
        const [visible, overflow] = getOverflowElements(wrapper.current!, tags) || [];

        if (overflow?.length) {
            // Render visible items
            items.current!.innerHTML = '';
            items.current!.append(<Fragment>{visible}</Fragment>);

            // Render overflow
            dropdown.current!.append(
                <Fragment>
                    <button class="tag-list__tag" onClick={toggleDropdown}>
                        <span class="tag-list__tag-label">{overflow.length}</span>
                        <BPDFunctionalSvg name="arrow-chevron-right" />
                    </button>

                    <div
                        class={`tag-list__dropdown-items tag-list__dropdown-items--${
                            !visible || visible.length <= 1 ? 'left' : 'right'
                        }`}>
                        {overflow}
                    </div>
                </Fragment>,
            );
        }
    }

    function renderTagList() {
        const tags = generateTags();

        items.current!.innerHTML = '';
        items.current!.append(
            <Fragment>
                {tags.length > 0 &&
                    tags.map(({ id, name, type, value, hasRangeValues }) => (
                        <button
                            class="tag-list__tag"
                            onClick={async () => {
                                updateFilter({
                                    id,
                                    checked: false,
                                    name,
                                    type,
                                    value,
                                    hasRangeValues,
                                });

                                const label = await transformTagLabel(name, value);

                                GTMPush({
                                    event: 'gtm.filters',
                                    eventCategory: 'Woonwensen',
                                    eventAction: `Remove ${name}`,
                                    eventLabel: label.toString(),
                                });
                            }}
                            js-hook-tag-list-item>
                            <span class="tag-list__tag-label">
                                {transformTagLabel(name, value)}
                            </span>
                            <span class="tag-list__tag-icon">
                                <BPDFunctionalSvg name="icons-close" />
                            </span>
                        </button>
                    ))}
            </Fragment>,
        );
    }

    return (
        <div class="tag-list__wrapper" js-hook-tag-list-wrapper>
            <div class="tag-list__items" js-hook-tag-list-items />
            <div class="tag-list__dropdown" js-hook-tag-list-dropdown />
        </div>
    );
};

TagList.props = {
    disableCollapse: {
        type: Boolean,
        reflectToAttr: true,
    },
};

defineElement('bpd-tag-list', TagList);
